import type { NavLink } from '#core/types'
import type { Collections } from '@nuxt/content'
import type { ArticleGroup } from './article-groups'

interface ParsedContentFile {
  page?: boolean
  path: string
  title: string
  introduction?: string
  headline?: string
  children?: ParsedContentFile[]
}

const LESSONS_PATH = '/academy/lessons'

function reorderLessonSections(items: AcademyNavGroup[]) {
  return items.map((item) => {
    if (item.path !== LESSONS_PATH || !item.children?.length) {
      return item
    }
    const children = [...item.children].sort(
      (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity),
    )
    return { ...item, children }
  })
}

function pruneHiddenLessons(items: AcademyNavGroup[], hidden: Set<string>): AcademyNavGroup[] {
  const visible: AcademyNavGroup[] = []
  for (const item of items) {
    if (item.draft) {
      hidden.add(item.path)
      continue
    }
    if (item.children?.length) {
      const prunedChildren = pruneHiddenLessons(item.children as AcademyNavGroup[], hidden)
      visible.push({ ...item, children: prunedChildren as AcademyContentFile[] })
    }
    else {
      visible.push(item)
    }
  }
  return visible
}

export function findArticleSurroundings(
  path: string,
  nav: AcademyNavGroup[],
): { prev: AcademyContentFile | null, next: AcademyContentFile | null } {
  const flat: AcademyContentFile[] = []
  const walk = (items: AcademyContentFile[]) => {
    for (const item of items) {
      const children = (item as AcademyNavGroup).children
      if (children?.length) {
        walk(children)
      }
      else {
        flat.push(item)
      }
    }
  }
  walk(nav)

  const i = flat.findIndex(item => item.path === path)
  if (i === -1) {
    return { prev: null, next: null }
  }
  return {
    prev: i > 0 ? flat[i - 1]! : null,
    next: i < flat.length - 1 ? flat[i + 1]! : null,
  }
}

// `to` is never read; it only gives the weak children type an overlap with NavLink
export function firstChildToIfDir(item: Pick<ParsedContentFile, 'page' | 'path'> & { children?: { path?: string, to?: string }[] }) {
  if (!item.page && item.children?.[0]) {
    return item.children[0]?.path
  }
  return item.path
}

let academyNavPromise: Promise<{ data: Ref<AcademyNavGroup[]> }> | null = null
const academyNav = ref<AcademyNavGroup[]>([])
export const hiddenLessonPaths = ref<Set<string>>(new Set())

// Nav queries return slim nodes, so only `title` and `path` are guaranteed. `icon` is omitted
// to keep the collection's string from intersecting NavLink's icon-literal union, and `body`
// because its recursive minimark tree blows TypeScript's instantiation-depth limit (TS2589).
export type AcademyContentFile = Pick<Collections['pages'], 'title' | 'path'> & Partial<Omit<Collections['pages'], 'icon' | 'body' | 'title' | 'path'>> & NavLink & {
  wistiaId?: string
  ogImage?: string
  publishedAt?: string
  updatedAt?: string
  heroImage?: string
  description?: string
  draft?: boolean
}

export interface AcademyNavGroup extends AcademyContentFile {
  children?: AcademyContentFile[]
  groups?: ArticleGroup[]
}

export function fetchAcademyNav() {
  interface AcademyNavResponse {
    data: Ref<AcademyNavGroup[]>
  }
  academyNavPromise = academyNavPromise || new Promise<AcademyNavResponse>((resolve) => {
    queryCollectionNavigation('pages', ['icon', 'order', 'draft']).then((res) => {
      const hidden = new Set<string>()
      const children = (res[0]?.children ?? []) as AcademyNavGroup[]
      academyNav.value = reorderLessonSections(pruneHiddenLessons(children, hidden))
      hiddenLessonPaths.value = hidden
      resolve({ data: academyNav })
    })
  })
  return { data: academyNav, ...academyNavPromise } as AcademyNavResponse & Promise<AcademyNavResponse>
}
